import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
var _this = this;
var _excluded = ["children", "className"],
  _excluded2 = ["isOpen", "position", "size", "onClose", "title", "children", "className", "hasNoScroll", "overwrites", "innerRef"],
  _excluded3 = ["isOpen", "position", "size", "onClose", "title", "className", "children", "overwrites", "hasNoScroll", "innerRef"];
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import Helmet from 'react-helmet';
import { useMotionValue, useTransform } from 'framer-motion';
import getConfig from '../../js/settings';
import { Box, Flex, FlexItem, Backdrop, Heading, Link, Icon, useBreakpoint } from '../';
import { appendHtmlClass, isServer, getOverwrites, getAnimationEasing, getAnimationDuration, getPortalTarget } from '../../js/util';
import './_index.scss';
var DRAWER_SIZE = getConfig('DRAWER_SIZE');
var DRAWER_ANIMATION_DURATION = getConfig('DRAWER_ANIMATION_DURATION');
var PaneSection = function PaneSection(_ref) {
  var children = _ref.children,
    className = _ref.className,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  className = appendHtmlClass(className, 'pane-section');
  return /*#__PURE__*/React.createElement(FlexItem, Object.assign({
    as: "section",
    className: className
  }, rest), children);
};
var Pane = function Pane(_ref2) {
  var isOpen = _ref2.isOpen,
    position = _ref2.position,
    size = _ref2.size,
    onClose = _ref2.onClose,
    title = _ref2.title,
    children = _ref2.children,
    className = _ref2.className,
    hasNoScroll = _ref2.hasNoScroll,
    overwrites = _ref2.overwrites,
    innerRef = _ref2.innerRef,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  var iconPadding = 2;
  var titleMarginTop = -1;
  var swipeMinPx = 150;
  var mx = useMotionValue(0);
  var dx = useTransform(mx, function (i) {
    return position === 'left' && i > 0 || position === 'right' && i < 0 ? 0 : i;
  });
  var _useState = useState(false),
    isScrollable = _useState[0],
    setIsScrollable = _useState[1];
  useEffect(function () {
    var onTouchMove = function onTouchMove(event) {
      var contains = innerRef && innerRef.current && (innerRef.current === event.target || innerRef.current.contains(event.target));
      if (!isScrollable && contains) event.stopPropagation();
    };
    document.documentElement.addEventListener('touchmove', onTouchMove);
    return function () {
      return document.documentElement.removeEventListener('touchmove', onTouchMove);
    };
  }, [innerRef, isScrollable]);
  var close = useCallback(function (source, event) {
    if (typeof onClose === 'function') onClose({
      source: source
    }, event);
  }, [onClose]);
  var onCloseClick = function onCloseClick(event) {
    close('icon', event);
  };
  var onDragEnd = useCallback(function (event, info) {
    if ((position === 'right' && info.offset.x > swipeMinPx || position === 'left' && info.offset.x < swipeMinPx * -1) && Math.abs(info.velocity.x) > swipeMinPx) {
      close('gesture', event);
    }
  }, [position, swipeMinPx, close]);
  var onDragStart = useCallback(function (event, info) {
    setIsScrollable(Math.abs(info.delta.y) < Math.abs(info.delta.x));
  }, [setIsScrollable]);
  className = appendHtmlClass(className, 'drawer-pane');
  var ICON_SIZE_SCALE = getConfig('ICON_SIZE_SCALE');
  var _useBreakpoint = useBreakpoint(DRAWER_SIZE[size]),
    span = _useBreakpoint.span,
    width = _useBreakpoint.width,
    padding = _useBreakpoint.padding,
    titleSize = _useBreakpoint.titleSize,
    iconSize = _useBreakpoint.iconSize;
  var dimensions = position === 'left' || position === 'right' ? {
    width: width,
    height: '100%'
  } : {
    width: '100%',
    height: width
  };
  var iconScale = ICON_SIZE_SCALE.find(function (i) {
    return i.key === iconSize;
  });
  var iconWidth = iconScale ? iconScale.value : 0;
  var titlePadding = [padding + titleMarginTop, iconPadding * 2 + iconWidth, 0, padding];
  var visible = {
    left: 0,
    top: 0,
    visibility: 'visible'
  };
  var hidden = {
    left: '100%',
    transitionEnd: {
      visibility: 'hidden'
    }
  };

  // eslint-disable-next-line default-case
  switch (position) {
    case 'left':
      hidden = _objectSpread(_objectSpread({}, hidden), {}, {
        left: '-100%'
      });
      break;
    case 'top':
      hidden = _objectSpread(_objectSpread({}, hidden), {}, {
        top: '-100%'
      });
      break;
    case 'bottom':
      hidden = _objectSpread(_objectSpread({}, hidden), {}, {
        top: '100%'
      });
      break;
  }
  var variants = {
    visible: visible,
    hidden: hidden
  };
  var initial = 'hidden';
  var transition = {
    ease: getAnimationEasing('ease'),
    duration: getAnimationDuration(DRAWER_ANIMATION_DURATION)
  };
  var drag = useMemo(function () {
    var args = {
      style: {
        x: dx
      },
      drag: 'x',
      dragConstraints: {
        left: 0,
        right: 0,
        top: 0,
        bottom: 0
      },
      onDragEnd: onDragEnd,
      onDragStart: onDragStart,
      dragDirectionLock: true
    };
    return position === 'left' || position === 'right' ? args : {};
  }, [position, onDragEnd, dx, onDragStart]);
  var sections = children;
  var flexOverwrites = getOverwrites(overwrites, 'Flex');
  if (hasNoScroll) {
    sections = React.Children.map(children, function (i, index) {
      var paddingBottom = index + 1 < React.Children.count(children) ? 0 : padding;
      return /*#__PURE__*/React.cloneElement(i, {
        paddingLeft: padding,
        paddingRight: padding,
        paddingBottom: paddingBottom
      });
    });
  }
  return /*#__PURE__*/React.createElement(FlexItem, Object.assign({
    className: className,
    bg: ['white'],
    span: span,
    width: width,
    shadow: 5,
    isAnimated: true,
    variants: variants,
    initial: initial,
    transition: transition
  }, dimensions, drag, rest), /*#__PURE__*/React.createElement(Flex, Object.assign({
    height: "100%",
    flow: "column",
    alignItemsY: "stretch"
  }, flexOverwrites), title && /*#__PURE__*/React.createElement(FlexItem, {
    as: "hgroup",
    className: "drawer-title-wrapper",
    padding: titlePadding,
    span: "shrink"
  }, /*#__PURE__*/React.createElement(Heading, {
    className: "drawer-title",
    importance: 3,
    size: titleSize
  }, title)), hasNoScroll ? sections : /*#__PURE__*/React.createElement(PaneSection, {
    span: "auto",
    padding: [0, padding, padding],
    innerRef: innerRef
  }, sections)), /*#__PURE__*/React.createElement(Link, {
    className: "icon-close-link",
    type: "raw",
    onClick: onCloseClick
  }, /*#__PURE__*/React.createElement(Box, {
    padding: iconPadding,
    className: "icon-close"
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "times",
    size: iconSize,
    display: "block",
    color: ['gray']
  }))));
};
var Drawer = function Drawer(_ref3) {
  var isOpen = _ref3.isOpen,
    position = _ref3.position,
    size = _ref3.size,
    onClose = _ref3.onClose,
    title = _ref3.title,
    className = _ref3.className,
    children = _ref3.children,
    overwrites = _ref3.overwrites,
    hasNoScroll = _ref3.hasNoScroll,
    innerRef = _ref3.innerRef,
    rest = _objectWithoutPropertiesLoose(_ref3, _excluded3);
  var ESC_KEY_CODE = 27;
  useEffect(function () {
    document.addEventListener('keyup', onKeyUp.bind(_this));
    return function () {
      return document.removeEventListener('keyup', onKeyUp.bind(_this));
    };
  });
  var _useState2 = useState(false),
    isAnimating = _useState2[0],
    setIsAnimating = _useState2[1];
  if (isServer()) return null;
  var onBackdropClick = function onBackdropClick() {
    if (isOpen && typeof onClose === 'function') onClose({
      source: 'backdrop'
    });
  };
  var onKeyUp = function onKeyUp(e) {
    if (isOpen && e.keyCode === ESC_KEY_CODE && typeof onClose === 'function') onClose({
      source: 'key'
    });
  };
  var onCloseClick = function onCloseClick(info, event) {
    if (isOpen && typeof onClose === 'function') onClose(info, event); // check
  };

  var onAnimationComplete = function onAnimationComplete() {
    setIsAnimating(false);
  };
  var onAnimationStart = function onAnimationStart() {
    setIsAnimating(true);
  };
  className = appendHtmlClass(className, 'drawer');
  if (isOpen) className = appendHtmlClass(className, 'open');
  var paneOverwrites = getOverwrites(overwrites, 'Pane');
  var flow = position === 'top' || position === 'bottom' ? 'column' : 'row';
  var alignItemsX = position === 'top' || position === 'left' ? 'flex-start' : 'flex-end';
  var animate = isOpen ? 'visible' : 'hidden';
  var visible = {
    x: 0,
    opacity: 1,
    zIndex: 'auto',
    transition: {
      duration: 0
    }
  };
  var hidden = {
    x: '100%',
    opacity: 0,
    zIndex: -9999,
    transition: {
      duration: 0,
      when: 'afterChildren'
    }
  };
  var variants = {
    visible: visible,
    hidden: hidden
  };
  var drawer = /*#__PURE__*/React.createElement(Flex, Object.assign({
    as: "aside",
    className: className,
    flow: flow,
    alignItemsX: alignItemsX,
    isAnimated: true,
    animate: animate,
    variants: variants,
    onAnimationStart: onAnimationStart,
    onAnimationComplete: onAnimationComplete
  }, rest), /*#__PURE__*/React.createElement(Backdrop, {
    isOpen: isOpen,
    onClick: onBackdropClick
  }), /*#__PURE__*/React.createElement(Pane, Object.assign({
    isOpen: isOpen,
    position: position,
    size: size,
    onClose: onCloseClick,
    title: title,
    hasNoScroll: hasNoScroll,
    innerRef: innerRef
  }, paneOverwrites), children));
  var helmet = isOpen || isAnimating ? /*#__PURE__*/React.createElement(Helmet, null, /*#__PURE__*/React.createElement("html", {
    "data-drawer-open": true
  })) : null;
  return /*#__PURE__*/React.createElement(React.Fragment, null, helmet, /*#__PURE__*/ReactDOM.createPortal(drawer, getPortalTarget()));
};
Drawer.defaultProps = {
  isOpen: false,
  position: 'right',
  size: 'm',
  hasNoScroll: false
};
export { Drawer, PaneSection };